import { useId } from 'react';
import type { ExpiryChoice } from '../lib/expiry';
import { fmtDate, toDateInput } from '../lib/format';

/**
 * Explicit end-date choice. Neither option is preselected: the administrator must pick
 * "Einddatum kiezen" or "Geen einddatum" (specs/01 › Geen automatisch verval).
 */
export function ExpiryChoiceField({
  value,
  onChange,
  error,
  legend = 'Einddatum toegang',
  groupValidUntil,
}: {
  value: ExpiryChoice;
  onChange: (v: ExpiryChoice) => void;
  error?: string | null;
  legend?: string;
  groupValidUntil?: string | null;
}) {
  const id = useId();
  const name = `expiry-${id}`;
  const dateId = `${id}-date`;
  const errId = `${id}-err`;
  const max = groupValidUntil ? toDateInput(new Date(groupValidUntil)) : undefined;
  return (
    <fieldset className="field">
      <legend>{legend}</legend>
      <p className="hint" style={{ marginBottom: 4 }}>
        Toegang vervalt nooit vanzelf. Kies bewust.
        {groupValidUntil && <> De groep loopt tot {fmtDate(groupValidUntil)}.</>}
      </p>
      <div
        className="choice-group"
        role="radiogroup"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errId : undefined}
      >
        <label className="choice">
          <input
            type="radio"
            name={name}
            value="until"
            checked={value.mode === 'until'}
            onChange={() => onChange({ mode: 'until', date: value.mode === 'until' ? value.date : '' })}
          />
          <div>
            <strong>Einddatum kiezen</strong>
            <span>Toegang stopt aan het eind van die dag.</span>
          </div>
        </label>
        <label className="choice">
          <input
            type="radio"
            name={name}
            value="none"
            checked={value.mode === 'none'}
            onChange={() => onChange({ mode: 'none' })}
          />
          <div>
            <strong>Geen einddatum</strong>
            <span>{groupValidUntil ? 'Loopt mee met de groep.' : 'Blijft geldig tot je het intrekt.'}</span>
          </div>
        </label>
      </div>
      {value.mode === 'until' && (
        <div className="field" style={{ marginTop: 8 }}>
          <label htmlFor={dateId}>Laatste dag met toegang</label>
          <input
            id={dateId}
            className="input"
            type="date"
            value={value.date}
            min={toDateInput(new Date())}
            max={max}
            onChange={(e) => onChange({ mode: 'until', date: e.target.value })}
            style={{ maxWidth: 220 }}
          />
        </div>
      )}
      {error && (
        <span className="error" id={errId} role="alert">
          {error}
        </span>
      )}
    </fieldset>
  );
}
